import { type JSX, type ReactNode, useEffect } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

import { setRequestLocale } from '@/shared/api';
import { i18n, LOCALE_STORAGE_KEY } from '@/shared/i18n';

interface LocaleProviderProps {
  children: ReactNode;
}

/**
 * Direction is a document-level concern; components stay direction-agnostic
 * through logical properties (CLAUDE.md §9).
 */
function DocumentDirection({ children }: LocaleProviderProps): JSX.Element {
  const { i18n: instance } = useTranslation();

  // synchronizing with an external system (the document) — the one legitimate
  // use of useEffect (CLAUDE.md §7.2)
  useEffect(() => {
    const root = document.documentElement;
    root.lang = instance.language;
    root.dir = instance.dir(instance.language);
    // the backend localizes error codes too, so every request follows the UI —
    // including the first one after a reload into a remembered language
    setRequestLocale(instance.language);

    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, instance.language);
    } catch {
      // unavailable storage costs only the memory of the choice, never the choice
    }
  }, [instance, instance.language]);

  return children as JSX.Element;
}

export function LocaleProvider({ children }: LocaleProviderProps): JSX.Element {
  return (
    <I18nextProvider i18n={i18n}>
      <DocumentDirection>{children}</DocumentDirection>
    </I18nextProvider>
  );
}
